<template>
  <div class="p2p-stack" style="gap:28px">
    <!-- What we learned -->
    <section v-if="reading" class="p2p-card is-accent">
      <div class="p2p-row"><span class="p2p-dot is-accent pulse"></span><h2 class="p2p-h2">Reading {{ host || 'your product' }}</h2></div>
      <p class="p2p-text">{{ reading.stage || 'Starting' }}… This usually takes under a minute. You can choose formats while we read.</p>
      <div class="p2p-progress"><span :style="{ width: readingProgress + '%' }"></span></div>
    </section>
    <section v-else-if="readingFailed" class="p2p-alert bad">
      <div class="p2p-stack" style="gap:8px">
        <strong>We could not read your product.</strong>
        <span>{{ project.latest_reading.error }}</span>
        <button type="button" class="p2p-btn sm soft" style="align-self:flex-start" @click="$emit('go', 'source')">Change the source</button>
      </div>
    </section>
    <section v-else-if="reReadFailed" class="p2p-alert">
      <div class="p2p-stack" style="gap:8px">
        <strong>We could not read {{ project.reading_url || 'that again' }}.</strong>
        <span>{{ project.latest_reading.error }} Your posts still use what we learned before, from {{ hostName(project.primary_url) || 'your notes' }}.</span>
        <button type="button" class="p2p-btn sm soft" style="align-self:flex-start" @click="$emit('go', 'source')">Change the source</button>
      </div>
    </section>
    <div v-if="knowledge && knowledge.confidence === 'low' && !editing" class="p2p-alert">
      <div class="p2p-stack" style="gap:8px">
        <strong>Please check what we learned.</strong>
        <span>We found little on the site, so some details below are our best guess. Correct anything that is wrong: every post uses these facts.</span>
        <button type="button" class="p2p-btn sm soft" style="align-self:flex-start" @click="editing = true">Edit details</button>
      </div>
    </div>
    <section v-if="knowledge" class="p2p-card" aria-labelledby="learned">
      <div class="p2p-between">
        <div class="p2p-stack" style="gap:6px">
          <span class="p2p-kicker">What we learned</span>
          <h2 id="learned" class="p2p-h2">{{ knowledge.name }}</h2>
          <p class="p2p-text">{{ knowledge.one_liner }}</p>
        </div>
        <button type="button" class="p2p-btn sm outline" @click="editing = !editing">{{ editing ? 'Close' : 'Edit details' }}</button>
      </div>
      <template v-if="!editing">
        <div v-if="knowledge.audience" class="p2p-muted"><strong>For:</strong> {{ knowledge.audience }}</div>
        <div v-if="brief" class="p2p-brief" aria-label="How posts introduce it">
          <span class="p2p-kicker">How posts introduce it</span>
          <p v-if="brief.pitch" class="p2p-brief-pitch">{{ brief.pitch }}</p>
          <dl v-if="briefRows.length" class="p2p-brief-rows">
            <div v-for="row in briefRows" :key="row.key" class="p2p-brief-row"><dt>{{ row.label }}</dt><dd>{{ row.value }}</dd></div>
          </dl>
          <ul v-if="brief.objections && brief.objections.length" class="p2p-brief-doubts">
            <li v-for="(doubt, i) in brief.objections" :key="i"><strong>{{ doubt.question }}</strong> {{ doubt.answer }}</li>
          </ul>
        </div>
        <span class="p2p-kicker" style="margin-top:4px">Facts the posts may use</span>
        <ul class="p2p-facts">
          <li v-for="fact in shownFacts" :key="fact.id" class="p2p-text">{{ fact.text }}</li>
        </ul>
        <button v-if="knowledge.facts.length > 5" type="button" class="p2p-btn ghost sm" style="align-self:flex-start;padding:0 6px" @click="allFacts = !allFacts">
          {{ allFacts ? 'Show fewer' : 'Show all ' + knowledge.facts.length + ' facts' }}
        </button>
        <div v-if="pictures.length" class="p2p-stack" style="gap:8px">
          <div class="p2p-between">
            <span class="p2p-kicker">Pictures for your posts</span>
            <span class="p2p-faint" role="status">{{ pictureStatus }}</span>
          </div>
          <div class="p2p-strip">
            <figure v-for="picture in pictures" :key="picture.id" :class="['p2p-strip-item', { 'is-off': !picture.used }]">
              <MediaImg :src="picture.url" ratio="4 / 3" :alt="picture.seen || picture.alt" />
              <div class="p2p-row" style="gap:6px">
                <span v-if="picture.role" class="p2p-chip sm">{{ picture.role }}</span>
                <span v-if="picture.upload" class="p2p-chip sm">Your upload</span>
              </div>
              <label class="p2p-pick"><input type="checkbox" :checked="picture.used" :disabled="savingPictures" @change="togglePicture(picture)"> Use in posts</label>
              <figcaption v-if="picture.seen" class="p2p-strip-caption" :title="picture.seen">{{ picture.seen }}</figcaption>
            </figure>
          </div>
        </div>
        <p v-if="seenCount" class="p2p-faint" style="margin:0">We looked at {{ seenCount === 1 ? 'one picture' : seenCount + ' pictures' }} and left out the ones that do not show your product well; untick any you do not want in posts. What we see in them is never used as a fact.</p>
        <span v-if="pictureError" class="p2p-faint p2p-bad">{{ pictureError }}</span>
        <div v-if="knowledge.brand_colors && knowledge.brand_colors.length" class="p2p-row p2p-faint" style="gap:8px">Brand colours, used in your post designs:
          <span v-for="color in knowledge.brand_colors" :key="color" class="p2p-row" style="gap:4px"><span class="p2p-swatch" :style="{ background: color }" aria-hidden="true"></span><span class="p2p-mono">{{ color }}</span></span>
        </div>
        <div class="p2p-row p2p-faint">
          <span v-if="knowledge.cta_text">Call to action: <strong>{{ knowledge.cta_text }}</strong> → {{ knowledge.cta_url }}</span>
          <span v-if="knowledge.unknowns && knowledge.unknowns.length">· Not on the site: {{ knowledge.unknowns.join(', ') }}</span>
        </div>
      </template>
      <div v-else class="p2p-stack">
        <label class="p2p-label">Product name<input v-model="edit.name" class="p2p-input" maxlength="80"></label>
        <label class="p2p-label">What it does<input v-model="edit.one_liner" class="p2p-input" maxlength="240"></label>
        <label class="p2p-label">Who it is for<input v-model="edit.audience" class="p2p-input" maxlength="300"></label>
        <div class="p2p-row" style="align-items:flex-end">
          <label class="p2p-label" style="flex:1 1 200px">Call to action<input v-model="edit.cta_text" class="p2p-input" maxlength="80"></label>
          <label class="p2p-label" style="flex:2 1 280px">Link<input v-model="edit.cta_url" class="p2p-input" maxlength="2048"></label>
        </div>
        <fieldset class="p2p-brief-edit">
          <legend class="p2p-kicker">How posts introduce it</legend>
          <span class="p2p-faint">Posts for people who have never heard of {{ edit.name || 'your product' }} start from these. Leave a line empty to let the posts work without it.</span>
          <label class="p2p-label">In one sentence<input v-model="edit.positioning.pitch" class="p2p-input" maxlength="240" placeholder="What it is, who it is for, and the result they get"></label>
          <div class="p2p-row" style="align-items:flex-end">
            <label class="p2p-label" style="flex:1 1 200px">What it is<input v-model="edit.positioning.category" class="p2p-input" maxlength="80" placeholder="For example: an AI carousel maker"></label>
            <label class="p2p-label" style="flex:2 1 280px">For whom<input v-model="edit.positioning.for_whom" class="p2p-input" maxlength="200"></label>
          </div>
          <label class="p2p-label">The pain it removes<input v-model="edit.positioning.problem" class="p2p-input" maxlength="280"></label>
          <label class="p2p-label">The result people get<input v-model="edit.positioning.outcome" class="p2p-input" maxlength="280"></label>
          <div class="p2p-label">How it works <span class="p2p-faint">up to 3 steps</span>
            <input v-for="(step, i) in edit.positioning.steps" :key="'step' + i" v-model="edit.positioning.steps[i]" class="p2p-input" maxlength="140" :aria-label="'Step ' + (i + 1)">
          </div>
          <div class="p2p-label">Why this one <span class="p2p-faint">up to 3 differences</span>
            <input v-for="(item, i) in edit.positioning.differentiators" :key="'diff' + i" v-model="edit.positioning.differentiators[i]" class="p2p-input" maxlength="180" :aria-label="'Difference ' + (i + 1)">
          </div>
          <label class="p2p-label">The offer<input v-model="edit.positioning.offer" class="p2p-input" maxlength="200" placeholder="For example: free to try, no card needed"></label>
          <div class="p2p-label">Doubts the posts answer <span class="p2p-faint">optional</span>
            <div v-for="(doubt, i) in edit.positioning.objections" :key="'doubt' + i" class="p2p-row" style="align-items:flex-end">
              <input v-model="doubt.question" class="p2p-input" style="flex:1 1 200px" maxlength="160" :aria-label="'Doubt ' + (i + 1)" placeholder="A question people ask">
              <input v-model="doubt.answer" class="p2p-input" style="flex:2 1 280px" maxlength="240" :aria-label="'Answer ' + (i + 1)" placeholder="Your answer">
            </div>
          </div>
        </fieldset>
        <div class="p2p-label">Facts the posts may use
          <div v-for="(fact, i) in edit.facts" :key="i" class="p2p-row" style="flex-wrap:nowrap">
            <input v-model="fact.text" class="p2p-input" maxlength="300" :aria-label="'Fact ' + (i + 1)">
            <button type="button" class="p2p-icon-btn" aria-label="Remove fact" @click="edit.facts.splice(i, 1)"><v-icon small>mdi-close</v-icon></button>
          </div>
          <button v-if="edit.facts.length < 24" type="button" class="p2p-btn ghost sm" style="align-self:flex-start" @click="edit.facts.push({ text: '', source: 'notes' })">+ Add a fact</button>
        </div>
        <div class="p2p-row">
          <button type="button" class="p2p-btn sm" :disabled="saving" @click="saveKnowledge">Save details</button>
          <span v-if="saveError" class="p2p-faint p2p-bad">{{ saveError }}</span>
        </div>
      </div>
    </section>

    <template v-if="!knowledgeOnly">
    <!-- Formats -->
    <section class="p2p-stack" aria-labelledby="formats-title">
      <div class="p2p-between">
        <h2 id="formats-title" class="p2p-h2">What should we make?</h2>
        <span class="p2p-muted">Up to {{ maxPosts }} posts at a time</span>
      </div>
      <div v-if="bestFormat" class="p2p-alert is-info">{{ bestFormat }} posts get the most interactions in this project so far.</div>
      <div class="p2p-grid" style="grid-template-columns:repeat(auto-fit,minmax(min(230px,100%),1fr))">
        <div v-for="item in formatCards" :key="item.key" :class="['p2p-card', 'raised', { 'is-accent': formats[item.key] > 0 }]">
          <div class="p2p-between">
            <span class="p2p-h3"><v-icon small color="#d6f34b">{{ item.icon }}</v-icon> {{ item.label }}</span>
            <span class="p2p-chip mono">{{ item.price }}</span>
          </div>
          <p class="p2p-muted" style="margin:0">{{ item.help }}</p>
          <div class="p2p-row p2p-stepper">
            <button type="button" class="p2p-icon-btn" :aria-label="'Fewer ' + item.label" :disabled="formats[item.key] === 0" @click="bump(item.key, -1)"><v-icon small>mdi-minus</v-icon></button>
            <span class="p2p-mono p2p-count">{{ formats[item.key] }}</span>
            <button type="button" class="p2p-icon-btn" :aria-label="'More ' + item.label" :disabled="formats[item.key] >= item.max || total >= maxPosts" @click="bump(item.key, 1)"><v-icon small>mdi-plus</v-icon></button>
          </div>
          <div v-if="item.key === 'image'" class="p2p-seg p2p-seg-fill" role="group" aria-label="Image style">
            <button v-for="mode in imageModes" :key="mode.key" type="button" :class="{ on: formats.image_mode === mode.key }" :aria-pressed="formats.image_mode === mode.key ? 'true' : 'false'" @click="formats.image_mode = mode.key">{{ mode.label }}</button>
          </div>
          <p v-if="item.key === 'image'" class="p2p-faint" style="margin:0">{{ imageModeHelp }}</p>
          <div v-if="item.key === 'carousel'" class="p2p-seg p2p-seg-fill" role="group" aria-label="Slides">
            <button v-for="count in [3, 5, 7]" :key="count" type="button" :class="{ on: formats.slides === count }" :aria-pressed="formats.slides === count ? 'true' : 'false'" @click="formats.slides = count">{{ count }} slides</button>
          </div>
          <div v-if="item.key === 'video'" class="p2p-seg" role="group" aria-label="Video style">
            <button type="button" :class="{ on: formats.video_style === 'motion' }" :aria-pressed="formats.video_style === 'motion' ? 'true' : 'false'" @click="formats.video_style = 'motion'">Motion</button>
            <button type="button" :class="{ on: formats.video_style === 'ai' }" :aria-pressed="formats.video_style === 'ai' ? 'true' : 'false'" :disabled="!aiVideoAvailable" :title="aiVideoAvailable ? '' : 'AI video comes with the Builder and Growth plans'" @click="formats.video_style = 'ai'">AI video</button>
          </div>
          <p v-if="item.key === 'video' && formats.video_style === 'ai'" class="p2p-faint" style="margin:0">
            About 10 seconds of vertical AI footage per clip, ready in a few minutes. {{ item.price }} each, charged only when the clip is made. AI footage can get details wrong, so watch it before you publish.</p>
          <p v-else-if="item.key === 'video'" class="p2p-faint" style="margin:0">About 10 to 15 seconds, made from your images with your words on screen.</p>
        </div>
      </div>
      <div v-if="formats.image || formats.carousel" class="p2p-label">Picture shape
        <div class="p2p-seg" role="group" aria-label="Picture shape">
          <button v-for="shape in shapes" :key="shape.key" type="button" :class="{ on: formats.shape === shape.key }" :aria-pressed="formats.shape === shape.key ? 'true' : 'false'" @click="formats.shape = shape.key">{{ shape.label }}</button>
        </div>
        <span class="p2p-faint">{{ shapeHelp }}</span>
      </div>
      <div v-if="project" class="p2p-row" style="gap:10px">
        <button type="button" class="p2p-btn sm soft" :disabled="savingStyle" @click="saveStyle">Save as this project's style</button>
        <span v-if="styleSaved" class="p2p-faint" role="status">Saved. New batches and autopilot posts start from this mix, picture style, shape and voice.</span>
      </div>
    </section>

    <!-- Direction -->
    <section class="p2p-card" aria-labelledby="direction-title">
      <h2 id="direction-title" class="p2p-h2">Direction <span class="p2p-faint" style="font-family:var(--font-body)">optional</span></h2>
      <div class="p2p-label">What the posts should do
        <div class="p2p-row" style="gap:6px" role="group" aria-label="Post goals">
          <button type="button" :class="['p2p-chip', { 'is-accent': hasGoal('auto') }]" style="cursor:pointer" :aria-pressed="hasGoal('auto') ? 'true' : 'false'" @click="toggleGoal('auto')">Best mix</button>
          <button v-for="goal in goals" :key="goal.key" type="button" :class="['p2p-chip', { 'is-accent': hasGoal(goal.key) }]" style="cursor:pointer" :aria-pressed="hasGoal(goal.key) ? 'true' : 'false'" @click="toggleGoal(goal.key)">{{ goal.label }}</button>
          <button v-if="!moreGoals" type="button" class="p2p-btn ghost sm" style="padding:0 6px" @click="moreGoals = true">More goals</button>
        </div>
        <div v-if="moreGoals" class="p2p-row" style="gap:6px" role="group" aria-label="Goals for people who know the product">
          <button v-for="goal in laterGoals" :key="goal.key" type="button" :class="['p2p-chip', { 'is-accent': hasGoal(goal.key) }]" style="cursor:pointer" :aria-pressed="hasGoal(goal.key) ? 'true' : 'false'" @click="toggleGoal(goal.key)">{{ goal.label }}</button>
        </div>
        <span class="p2p-faint">{{ goalHelp }}</span>
      </div>
      <div class="p2p-label">Voice
        <div class="p2p-seg" role="group" aria-label="Voice">
          <button v-for="tone in tones" :key="tone.key" type="button" :class="{ on: direction.tone === tone.key }" :aria-pressed="direction.tone === tone.key ? 'true' : 'false'" @click="direction.tone = tone.key">{{ tone.label }}</button>
        </div>
      </div>
      <label class="p2p-label" style="max-width:280px">Post language
        <select v-model="direction.language" class="p2p-select"><option v-for="item in languages" :key="item" :value="item">{{ item }}</option></select>
      </label>
      <label class="p2p-label">Anything to mention?
        <textarea v-model="direction.notes" class="p2p-textarea" rows="3" maxlength="1000" placeholder="For example: the new batch upload, or that the first conversion is free"></textarea>
      </label>
      <label class="p2p-label">Never say <span class="p2p-faint">comma separated</span>
        <input v-model="neverSay" class="p2p-input" placeholder="cheap, revolutionary">
      </label>
    </section>

    <div class="p2p-sticky">
      <div class="p2p-stack" style="gap:2px">
        <strong>{{ total }} {{ total === 1 ? 'post' : 'posts' }} · {{ estimateUpTo ? 'up to ' : '' }}{{ estimate }} credits</strong>
        <span class="p2p-faint">You have {{ available }} credits. Writing is charged per post; visuals only when they succeed{{ estimateUpTo ? ', and posts that show your own pictures cost less than AI pictures' : '' }}.</span>
      </div>
      <div class="p2p-row">
        <router-link v-if="estimate > available" :to="creditsLink" class="p2p-btn outline">{{ hasPlan ? 'Add credits' : 'Choose a plan' }}</router-link>
        <button type="button" class="p2p-btn lg" :disabled="!canGenerate" @click="generate">
          {{ busy ? 'Starting…' : 'Generate ' + total + ' ' + (total === 1 ? 'post' : 'posts') }}
          <v-icon small color="#0b0b10">mdi-arrow-right</v-icon>
        </button>
      </div>
    </div>
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    </template>
  </div>
</template>

<script>
import MediaImg from '../MediaImg.vue';
import { get, post, patch, message } from '../../api';
import { poller, active, hostOf } from '../../poll';
import { GOALS, LATER_GOALS, LEGACY_GOALS, ROLE_LABELS } from '../../goals';

const IMAGE_MODES = [{ key: 'best', label: 'Best fit' }, { key: 'mine', label: 'Your images' }, { key: 'ai', label: 'AI pictures' }];

// What the image-reading look leaves out unless the member picks it (mirrors visuals.offered on the server).
function offeredByDefault(note) {
  return !!note && note.usable !== false && note.role !== 'logo' && note.role !== 'text_graphic';
}

function emptyBrief() {
  return { category: '', for_whom: '', problem: '', outcome: '', steps: [], differentiators: [], offer: '', objections: [], pitch: '', fact_ids: [] };
}

function padded(list, size, blank) {
  const items = (list || []).slice(0, size);
  while (items.length < size) items.push(blank());
  return items;
}

export default {
  name: 'StepFormats',
  components: { MediaImg },
  props: { project: { type: Object, default: null }, config: { type: Object, default: null }, workspace: { type: Object, default: null },
    knowledgeOnly: { type: Boolean, default: false } },
  data: () => ({ formats: { text: 3, image: 2, image_mode: 'best', carousel: 1, slides: 5, video: 0, video_style: 'motion', shape: 'portrait' },
    shapes: [{ key: 'portrait', label: 'Portrait 4:5' }, { key: 'square', label: 'Square 1:1' }, { key: 'landscape', label: 'Landscape' }, { key: 'story', label: 'Story 9:16' }],
    direction: { angles: ['auto'], tone: 'founder', notes: '', language: 'English' }, neverSay: '', busy: false, error: '', loop: null, loadedSettings: false,
    savingStyle: false, styleSaved: false,
    best: null,
    editing: false, edit: null, saving: false, saveError: '', allFacts: false, openedFromLink: false,
    goals: GOALS, laterGoals: LATER_GOALS, moreGoals: false, imageModes: IMAGE_MODES,
    savingPictures: false, pictureError: '',
    tones: [{ key: 'founder', label: 'Founder' }, { key: 'professional', label: 'Professional' }, { key: 'playful', label: 'Playful' }, { key: 'bold', label: 'Bold' }] }),
  computed: {
    knowledge() { return this.project && this.project.knowledge_ready ? this.project.knowledge : null; },
    reading() { const op = this.project && this.project.latest_reading; return op && active(op) ? op : null; },
    readingFailed() { const op = this.project && this.project.latest_reading; return !!(op && op.status === 'failed' && !this.knowledge); },
    readingProgress() {
      const stages = ['Reading your site', 'Collecting images from your site', 'Summarising your product', 'Looking at your images'];
      const index = this.reading ? stages.indexOf(this.reading.stage) : -1;
      return index < 0 ? 12 : 30 + index * 20;
    },
    // Pictures the image-reading look described: the site's and the member's uploads.
    seenCount() {
      if (!this.knowledge) return 0;
      const uploads = Object.keys(this.knowledge.upload_notes || {}).length || Object.keys(this.knowledge.uploads_seen || {}).length;
      return this.knowledge.images.filter(image => image.seen).length + uploads;
    },
    // The positioning brief (FR-KNW-001), when the reading wrote one.
    brief() {
      const value = this.knowledge && this.knowledge.positioning;
      if (!value) return null;
      const filled = value.pitch || value.category || value.problem || value.outcome || (value.steps || []).length || value.offer;
      return filled ? value : null;
    },
    briefRows() {
      const value = this.brief;
      if (!value) return [];
      const audience = (this.knowledge.audience || '').trim().toLowerCase();
      // "For" is the label, so a leading "For" in the brief's own words is dropped.
      const forWhom = (value.for_whom || '').trim().replace(/^for\s+/i, '');
      const rows = [
        { key: 'category', label: 'What it is', value: value.category },
        { key: 'for_whom', label: 'For', value: forWhom.toLowerCase() === audience ? '' : forWhom },
        { key: 'problem', label: 'The pain', value: value.problem },
        { key: 'outcome', label: 'The result', value: value.outcome },
        { key: 'steps', label: 'How it works', value: (value.steps || []).join(' → ') },
        { key: 'differentiators', label: 'Why this one', value: (value.differentiators || []).join(' · ') },
        { key: 'offer', label: 'The offer', value: value.offer }
      ];
      return rows.filter(row => row.value);
    },
    // Every picture posts could show: the site's, then the member's uploads, with what the look made of them.
    pictures() {
      if (!this.knowledge) return [];
      const choices = (this.project.settings && this.project.settings.pictures) || {};
      const notes = this.knowledge.upload_notes || {};
      const describe = (id, url, alt, note, upload) => {
        const chosen = Object.prototype.hasOwnProperty.call(choices, id) ? choices[id] : null;
        return { id, url, alt, upload, seen: (note && note.seen) || '', role: note && note.role ? ROLE_LABELS[note.role] || '' : '',
          used: chosen === null ? offeredByDefault(note || {}) : chosen };
      };
      const found = this.knowledge.images.map(image => describe(image.id, image.url, image.alt, image, false));
      (this.project.sources || []).forEach(source => {
        if (source.kind === 'image' && source.status === 'complete' && source.media && source.media.url) {
          found.push(describe(source.ref, source.media.url, source.filename || 'Your upload', notes[source.ref] || null, true));
        }
      });
      return found;
    },
    pictureStatus() {
      if (this.savingPictures) return 'Saving…';
      const used = this.pictures.filter(picture => picture.used).length;
      return used + ' of ' + this.pictures.length + ' used';
    },
    imageModeHelp() {
      const notes = { best: 'Your real pictures where they prove the point; a new AI picture only when none fits. Never a picture that does not belong.',
        mine: 'Only your own pictures, shown whole. A clean headline card when none fits the post.',
        ai: 'A new AI picture for every post, styled after your site. Your product screens are never faked.' };
      return notes[this.formats.image_mode] || '';
    },
    goalHelp() {
      if (this.hasGoal('auto')) return 'Best mix introduces your product to people who have never heard of it first: what it is, the result, the pain it removes and the offer.';
      return 'Each post takes the next goal you picked, in turn.';
    },
    host() { return this.project ? hostOf((this.reading && this.project.reading_url) || this.project.primary_url) : ''; },
    // A re-read that failed while earlier knowledge exists: say so, and that the earlier reading still applies.
    reReadFailed() { const op = this.project && this.project.latest_reading; return !!(op && op.status === 'failed' && this.knowledge); },
    shownFacts() { return this.knowledge ? (this.allFacts ? this.knowledge.facts : this.knowledge.facts.slice(0, 5)) : []; },
    prices() { return this.config ? this.config.prices : null; },
    maxPosts() { return this.config ? this.config.batch_max_posts : 12; },
    languages() { return this.config && this.config.languages ? this.config.languages : ['English']; },
    bestFormat() { return this.best ? ({ text: 'Text', image: 'Image', carousel: 'Carousel', video: 'Video' }[this.best] || this.best) : ''; },
    aiVideoAvailable() { return !!(this.config && this.config.ai_video_available && this.workspace && this.workspace.ai_video_access); },
    formatCards() {
      const p = this.prices || { text: '1', image: { mine: '2', ai: '6', best: '6' }, image_best_from: '2', carousel: { 3: '8', 5: '12', 7: '16' }, video: { motion: '8', ai: '61' } };
      const imagePrice = this.formats.image_mode === 'best' ? (p.image_best_from || p.image.mine) + '–' + p.image.best : p.image[this.formats.image_mode];
      return [
        { key: 'text', label: 'Text', icon: 'mdi-text', max: 6, price: p.text + ' credit', help: 'Founder-voice posts, with a thread version for X when they run long.' },
        { key: 'image', label: 'Image', icon: 'mdi-image-outline', max: 4, price: imagePrice + ' credits', help: 'One picture that makes the point: a before and after, your product at work, or a headline card.' },
        { key: 'carousel', label: 'Carousel', icon: 'mdi-view-carousel-outline', max: 2, price: p.carousel[String(this.formats.slides)] + ' credits', help: 'Swipeable slides that walk through one idea.' },
        { key: 'video', label: 'Video', icon: 'mdi-movie-open-outline', max: 2, price: p.video[this.formats.video_style] + ' credits', help: 'A short vertical clip: motion from your images, or AI footage.' }
      ];
    },
    total() { return this.formats.text + this.formats.image + this.formats.carousel + this.formats.video; },
    shapeHelp() {
      const notes = { portrait: 'Tallest feed post: best on Instagram, LinkedIn and Facebook.', square: 'Works everywhere.',
        landscape: 'Wide link-preview shape for X, LinkedIn and Bluesky. Carousels stay portrait.',
        story: 'Full-screen 9:16 for TikTok and Pinterest. Not for Instagram feed posts. Carousels stay portrait.' };
      return notes[this.formats.shape] || '';
    },
    estimate() {
      const p = this.prices;
      if (!p) return 0;
      return Number(p.text) * this.formats.text + Number(p.image[this.formats.image_mode]) * this.formats.image +
        Number(p.carousel[String(this.formats.slides)]) * this.formats.carousel + Number(p.video[this.formats.video_style]) * this.formats.video;
    },
    // 'Best for each post' is charged as what each picture turns out to be, so the estimate is an upper bound.
    estimateUpTo() { return this.formats.image_mode === 'best' && this.formats.image > 0; },
    available() { return this.workspace ? Math.floor(Number(this.workspace.available_credits)) : 0; },
    hasPlan() { return !!(this.workspace && this.workspace.paid_access); },
    // Top-ups are for members with a plan (on Billing); everyone else starts with a plan.
    creditsLink() { return this.hasPlan ? '/billing' : { path: '/subscribe', query: { next: this.$route.fullPath } }; },
    canGenerate() { return !this.busy && this.total > 0 && !!this.knowledge && this.estimate <= this.available; }
  },
  watch: {
    project: { immediate: true, handler(value) {
      if (value && value.settings && !this.loadedSettings) {
        this.loadedSettings = true;
        Object.assign(this.formats, value.settings.formats);
        Object.assign(this.direction, value.settings.direction);
        // Earlier angle names map onto the goals that replaced them.
        const goals = (this.direction.angles || ['auto']).map(goal => LEGACY_GOALS[goal] || goal);
        this.direction.angles = goals.filter((goal, i) => goals.indexOf(goal) === i);
        this.moreGoals = LATER_GOALS.some(goal => this.hasGoal(goal.key));
        this.neverSay = (value.settings.never_say || []).join(', ');
        if (this.formats.video_style === 'ai' && !this.aiVideoAvailable) this.formats.video_style = 'motion';
        if (!this.knowledgeOnly) get('/projects/' + value.id + '/analytics').then(data => { this.best = data.best_format; }).catch(() => {});
      }
      if (value && value.knowledge && !this.edit) this.resetEdit();
      if (value && value.knowledge && this.$route.query.edit === '1' && !this.openedFromLink) {
        this.openedFromLink = true;
        this.editing = true;
      }
      this.watchReading();
    } }
  },
  beforeDestroy() { if (this.loop) this.loop.stop(); },
  methods: {
    watchReading() {
      if (!this.reading || this.loop) return;
      this.loop = poller(async () => {
        this.$emit('refresh');
        if (!this.reading) this.loop = null;
        return !!this.reading;
      }, 3000);
      this.loop.start();
    },
    resetEdit() {
      const k = this.project.knowledge;
      const brief = { ...emptyBrief(), ...(k.positioning || {}) };
      this.edit = { name: k.name, one_liner: k.one_liner, audience: k.audience, cta_text: k.cta_text, cta_url: k.cta_url, tone: k.tone,
        facts: k.facts.map(fact => ({ text: fact.text, source: fact.source, quote: fact.quote })), brand_colors: k.brand_colors,
        positioning: { ...brief, steps: padded(brief.steps, 3, () => ''), differentiators: padded(brief.differentiators, 3, () => ''),
          objections: padded((brief.objections || []).map(doubt => ({ ...doubt })), 3, () => ({ question: '', answer: '' })) } };
    },
    // The brief as the server accepts it: blank lines dropped, a doubt only with its answer.
    cleanBrief() {
      const brief = this.edit.positioning;
      const lines = list => list.map(item => item.trim()).filter(item => item.length >= 3);
      return { ...brief, category: brief.category.trim(), for_whom: brief.for_whom.trim(), problem: brief.problem.trim(),
        outcome: brief.outcome.trim(), offer: brief.offer.trim(), pitch: brief.pitch.trim(),
        steps: lines(brief.steps), differentiators: lines(brief.differentiators),
        objections: brief.objections.map(doubt => ({ question: doubt.question.trim(), answer: doubt.answer.trim() }))
          .filter(doubt => doubt.question.length >= 3 && doubt.answer.length >= 3) };
    },
    hostName(value) { return hostOf(value); },
    bump(key, delta) { this.formats[key] = Math.max(0, this.formats[key] + delta); },
    hasGoal(key) { return this.direction.angles.indexOf(key) >= 0; },
    toggleGoal(key) {
      if (key === 'auto') { this.direction.angles = ['auto']; return; }
      let goals = this.direction.angles.filter(goal => goal !== 'auto');
      goals = this.hasGoal(key) ? goals.filter(goal => goal !== key) : goals.concat([key]);
      this.direction.angles = goals.length ? goals : ['auto'];
    },
    // Saved straight away: the next batch, autopilot post or new visual uses the member's choice.
    async togglePicture(picture) {
      this.savingPictures = true;
      this.pictureError = '';
      try {
        const settings = this.project.settings || {};
        const pictures = { ...(settings.pictures || {}), [picture.id]: !picture.used };
        this.$emit('project', await patch('/projects/' + this.project.id, { settings: { ...settings, pictures } }));
      } catch (error) {
        this.pictureError = message(error);
      } finally {
        this.savingPictures = false;
      }
    },
    async saveKnowledge() {
      this.saving = true;
      this.saveError = '';
      try {
        const knowledge = { ...this.edit, facts: this.edit.facts.filter(fact => fact.text.trim().length >= 3), positioning: this.cleanBrief() };
        const updated = await patch('/projects/' + this.project.id, { knowledge });
        this.$emit('project', updated);
        this.editing = false;
        this.edit = null;
        this.resetEdit();
      } catch (error) {
        this.saveError = message(error);
      } finally {
        this.saving = false;
      }
    },
    // FR-CAR-008: keep this visual template as the project's default without generating anything.
    async saveStyle() {
      this.savingStyle = true;
      this.styleSaved = false;
      this.error = '';
      try {
        const never = this.neverSay.split(',').map(item => item.trim().slice(0, 160)).filter(item => item.length >= 2).slice(0, 10);
        const settings = { ...(this.project.settings || {}), formats: { ...this.formats }, direction: { ...this.direction }, never_say: never };
        this.$emit('project', await patch('/projects/' + this.project.id, { settings }));
        this.styleSaved = true;
      } catch (error) {
        this.error = message(error);
      } finally {
        this.savingStyle = false;
      }
    },
    async generate() {
      this.busy = true;
      this.error = '';
      try {
        const never = this.neverSay.split(',').map(item => item.trim().slice(0, 160)).filter(item => item.length >= 2).slice(0, 10);
        const operation = await post('/projects/' + this.project.id + '/batches', { formats: this.formats, direction: this.direction, never_say: never });
        this.$store.dispatch('publisher/loadWorkspace');
        this.$emit('go', 'review', { batch: String(operation.id) });
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style>
.p2p-facts{margin:0;padding-left:18px;display:flex;flex-direction:column;gap:6px}
.p2p-strip{display:grid;grid-auto-flow:column;grid-auto-columns:minmax(140px,180px);gap:10px;overflow-x:auto;padding-bottom:4px;align-items:start}
.p2p-strip-item{margin:0;display:flex;flex-direction:column;gap:6px;min-width:0}
.p2p-strip-item.is-off .p2p-media-box{opacity:.38;filter:grayscale(.8)}
.p2p-chip.sm{padding:2px 8px;font-size:11px}
.p2p-strip-caption{font-size:12px;line-height:1.4;color:var(--p2p-muted);display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden}
.p2p-pick{display:flex;align-items:center;gap:6px;font-size:13px;color:var(--p2p-text);cursor:pointer}
.p2p-pick input{width:16px;height:16px;accent-color:#d6f34b;margin:0}
.p2p-brief{display:flex;flex-direction:column;gap:8px;padding:14px 16px;border:1px solid var(--p2p-line);border-radius:12px;background:rgba(214,243,75,.04)}
.p2p-brief-pitch{margin:0;font-size:16px;line-height:1.5;color:#fff}
.p2p-brief-rows{margin:0;display:grid;grid-template-columns:minmax(96px,max-content) 1fr;gap:6px 14px}
.p2p-brief-row{display:contents}
.p2p-brief-row dt{color:var(--p2p-muted);font-size:13px}
.p2p-brief-row dd{margin:0;font-size:14px;line-height:1.45}
.p2p-brief-doubts{margin:0;padding-left:18px;display:flex;flex-direction:column;gap:4px;font-size:13px;color:var(--p2p-muted)}
.p2p-brief-doubts strong{color:var(--p2p-text);font-weight:600}
.p2p-brief-edit{border:1px solid var(--p2p-line);border-radius:12px;padding:12px 14px 14px;margin:0;display:flex;flex-direction:column;gap:12px;min-width:0}
.p2p-brief-edit legend{padding:0 6px}
@media (max-width:520px){.p2p-brief-rows{grid-template-columns:1fr;gap:2px}.p2p-brief-row dd{margin-bottom:6px}}
.p2p-stepper{gap:14px}
.p2p-count{min-width:28px;text-align:center;font-size:22px;font-weight:600;color:#fff}
.p2p-seg button:disabled{opacity:.4;cursor:not-allowed}
.p2p-seg-fill{display:grid;grid-template-columns:repeat(3,minmax(0,1fr))}
.p2p-seg-fill button{padding:6px 8px;line-height:1.25}
.p2p-swatch{display:inline-block;width:14px;height:14px;border-radius:4px;border:1px solid var(--p2p-line)}
</style>
